import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import TextField from '@material-ui/core/TextField/TextField'
import clsx from 'clsx'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import React, { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAssemblyTextMutationHandler } from './useAssemblyTextMutationHandler'

/** Props for AssemblyText component */
interface AssemblyTextProps {
    /** The text content element containing the assembly text data */
    contentItem?: Partial<TextContentElement>
    /** Whether the text field should be editable. Defaults to false */
    editable?: boolean
    /** CSS classes for styling the component */
    classes?: any
}

/**
 * Multi-line text input for editing assembly text with optimistic updates
 * Saves changes on blur and validates max length (500 chars)
 */
export const AssemblyText = React.memo<AssemblyTextProps>(
    ({ contentItem, editable = false, classes }) => {
        const [currentValue, setCurrentValue] = useState(
            contentItem?.text || ''
        )
        const [newValue, setNewValue] = useState(currentValue)

        const { projectId } = useParams<{ projectId: string }>()

        const { handleUpdatePUCPropertyValue } = useAssemblyTextMutationHandler(
            {
                projectId,
            }
        )

        /** Updates local state on text change */
        const handleChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                setNewValue(event.target.value)
            },
            [setNewValue]
        )

        /** Saves changes to server if value differs from original */
        const handleBlur = useCallback(() => {
            if (!contentItem?.id || currentValue === newValue) return

            handleUpdatePUCPropertyValue(contentItem.id, newValue)
            setCurrentValue(newValue)
        }, [
            contentItem?.id,
            currentValue,
            newValue,
            handleUpdatePUCPropertyValue,
        ])

        /** Handles keyboard shortcuts for saving (Ctrl + S) */
        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent) => {
                const saveKey = tryGetShortcutKey('Save', event)

                if (saveKey) {
                    event.preventDefault()
                    handleBlur()
                }
            },
            [handleBlur]
        )

        // Update local state and ref when contentItem changes from parent
        React.useEffect(() => {
            const newText = contentItem?.text || ''
            setCurrentValue(newText)
            setNewValue(newText)
        }, [contentItem])

        return (
            <TextField
                variant="outlined"
                multiline
                placeholder={
                    contentItem?.placeholderText || 'Enter assembly text...'
                }
                className={clsx('assembly-text', classes?.assemblyTextField)}
                name="ElementPropertiesInput"
                value={newValue}
                inputProps={{
                    className: classes?.inputText,
                    'aria-autocomplete': 'none',
                    autoComplete: 'chrome-off',
                    maxLength: 500,
                }}
                data-testid="assembliesTextField"
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!editable}
                error={currentValue.length > 500}
            />
        )
    }
)

AssemblyText.displayName = 'AssemblyText'
